import React from 'react';
import { Text as RNText, TextProps } from 'react-native';
import { useTheme } from '../../hooks/useTheme';

interface CustomTextProps extends TextProps {
  variant?:
    | 'primary'
    | 'secondary'
    | 'default'
    | 'success'
    | 'warning'
    | 'error'
    | 'muted';
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  align?: 'left' | 'center' | 'right' | 'justify';
  transform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  lineHeight?: 'tight' | 'normal' | 'relaxed' | 'loose';
  opacity?: number;
}

/**
 * Enhanced Text component with comprehensive styling options
 * Based on the existing ThemedText but with more variations
 */
const Text: React.FC<CustomTextProps> = ({
  style,
  variant = 'default',
  weight = 'normal',
  size = 'md',
  align = 'left',
  transform = 'none',
  lineHeight = 'normal',
  opacity,
  children,
  ...props
}) => {
  const { colors } = useTheme();

  const getTextColor = () => {
    switch (variant) {
      case 'primary':
        return colors.primary;
      case 'secondary':
        return colors.secondary;
      case 'success':
        return '#10B981';
      case 'warning':
        return '#F59E0B';
      case 'error':
        return '#EF4444';
      case 'muted':
        return '#9CA3AF';
      case 'default':
      default:
        return colors.text;
    }
  };

  const getFontWeight = () => {
    switch (weight) {
      case 'light':
        return '300';
      case 'medium':
        return '500';
      case 'semibold':
        return '600';
      case 'bold':
        return '700';
      case 'extrabold':
        return '800';
      case 'normal':
      default:
        return '400';
    }
  };

  const getFontSize = () => {
    switch (size) {
      case 'xs':
        return 12;
      case 'sm':
        return 14;
      case 'md':
        return 16;
      case 'lg':
        return 18;
      case 'xl':
        return 20;
      case '2xl':
        return 24;
      case '3xl':
        return 30;
      default:
        return 16;
    }
  };

  const getTextAlign = () => {
    return align;
  };

  const getTextTransform = () => {
    return transform;
  };

  const getLineHeight = () => {
    const fontSize = getFontSize();
    switch (lineHeight) {
      case 'tight':
        return fontSize * 1.2;
      case 'relaxed':
        return fontSize * 1.6;
      case 'loose':
        return fontSize * 1.8;
      case 'normal':
      default:
        return fontSize * 1.4;
    }
  };

  return (
    <RNText
      style={[
        {
          color: getTextColor(),
          fontWeight: getFontWeight(),
          fontSize: getFontSize(),
          textAlign: getTextAlign(),
          textTransform: getTextTransform(),
          lineHeight: getLineHeight(),
          opacity: opacity,
        },
        style,
      ]}
      {...props}
    >
      {children}
    </RNText>
  );
};

// Pre-configured text components for common use cases
export const Heading1: React.FC<CustomTextProps> = (props) => (
  <Text size="3xl" weight="bold" {...props} />
);

export const Heading2: React.FC<CustomTextProps> = (props) => (
  <Text size="2xl" weight="semibold" {...props} />
);

export const Heading3: React.FC<CustomTextProps> = (props) => (
  <Text size="xl" weight="semibold" {...props} />
);

export const Subtitle: React.FC<CustomTextProps> = (props) => (
  <Text size="lg" weight="medium" variant="secondary" {...props} />
);

export const Body: React.FC<CustomTextProps> = (props) => (
  <Text size="md" {...props} />
);

export const Caption: React.FC<CustomTextProps> = (props) => (
  <Text size="sm" variant="muted" {...props} />
);

export const Label: React.FC<CustomTextProps> = (props) => (
  <Text size="sm" weight="medium" {...props} />
);

export default Text;
